import { Module } from 'lib/vendor/terrific/Module';
import { Hook } from 'lib/vendor/terrific/Sandbox';
import { DisplayIncidentType, ICustomerListData, IMessages } from './model';
import { compare, comparingBy, inverted } from 'lib/util/compare';


/**
 * Tc.Module.AbstractPexList
 */
export default abstract class AbstractPexList extends Module {

	protected dataUrl = '';
	protected data! : ICustomerListData[];
	protected abstract searchablesProps : Set<string>;
	protected labels! : IMessages;

	private $searchButton! : JQuery;
	private $searchField! : JQuery;
	private lastSearchTerm = '';
	private $openIncidentsCheckbox! : JQuery;
	private $closeIncidentsCheckbox! : JQuery;
	private displayStatus! : DisplayIncidentType;
	private sortPropName! : string;
	private sortAsc! : boolean;
	private $resultNumberHolder! : JQuery;
	private $exportToCSVButton! : JQuery;
	private $tableWrapper! : JQuery;

	/**
	 * Hook function to do all of your module stuff.
	 *
	 * @method on
	 * @param  {Hook} callback Callback function
	 * @return void
	 */
	public on(callback : Hook) {
		this.$searchButton = this.$ctx.find('.search-button');
		this.$searchField = this.$ctx.find('.search-field');
		this.$openIncidentsCheckbox = this.$ctx.find('#userdecidesOpenIncidents');
		this.$closeIncidentsCheckbox = this.$ctx.find('#userdecidesCloseIncidents');
		this.displayStatus = this.$ctx.data('show-incident-status');
		this.$resultNumberHolder = this.$ctx.find('.result-number');
		this.$exportToCSVButton = this.$ctx.find('.export-to-csv');
		this.labels = this.$ctx.data('labels');
		this.$tableWrapper = this.$ctx.find('.table__wrapper');

		this.addEventHandlers();
		this.init()
			.catch(err => this.log(`ERROR fetch data: ${err}`));

		callback();
	}

	public async init() {
		try {
			const data = await this.fetchData();
			data.forEach(item => {
				const date = new Date(item.creationDate);
				item.date = date.toLocaleDateString(document.documentElement.lang);
				item.creationDateTamestamp = date.getTime();
				item.searchValue = Object.keys(item)
				.filter(k => this.searchablesProps.has(k))
					.map(k => item[k])
					.filter(Boolean)
					.map(String)
					.map(prop => prop.toLowerCase())
					.join(' ');
			});

			this.data = data;
			this.filterData();
			this.render();
			this.updateResultNumber();
		} catch (err) {
			this.log(`ERROR fetch data: ${err.responseText}`);
		}
	}

	public onSort(evt : JQuery.ClickEvent) : void {
		const $target = $(evt.target);
		const propName = $target.data('sort-name');
		const asc = $target.hasClass('sort-up');

		$target.toggleClass('sort-up', !asc);
		$target.toggleClass('sort-down', asc);
		$target.siblings().removeClass('sort-up');
		$target.siblings().removeClass('sort-down');

		if(this.sortPropName === propName && this.sortAsc === asc) {
			return;
		}
		this.sortPropName = propName;
		this.sortAsc = asc;

		this.sort();
		this.update();
	}

	protected search() {
		const searchString = $.trim((this.$searchField.val() as string || '').toLowerCase());
		let allSearchTerms : string[];

		// If search string is same as prevous one search is already done.
		if(searchString === this.lastSearchTerm) {
			return;
		}
		this.lastSearchTerm = searchString;

		this.filterData();
		if(!this.lastSearchTerm) {
			return;
		}

		// Remove spaces after - and posible multiple accurance of - sign
		allSearchTerms = searchString.replace(/-+\s*/g, '-').split(/\s+/g);
		this.searchHelper(allSearchTerms);
	}

	protected filterData() {
		this.data.forEach(elem => {
			switch(this.displayStatus) {
				case 'open':
					elem.display = elem.status === 'open';
					break;
				case 'close':
					elem.display = elem.status === 'closed';
					break;
				case 'user-defined':
					elem.display = (elem.status === 'open' && this.$openIncidentsCheckbox.prop('checked')) ||
						(elem.status === 'closed' && this.$closeIncidentsCheckbox.prop('checked'));
					break;
				default:
					elem.display = true;
					break;
			}
		});
	}

	protected abstract render() : void;
	protected abstract update() : void;
	protected abstract exportToCSV() : void;

	protected async fetchData() {
		return $.post({
			data: {},
			url: this.dataUrl,
		}) as Promise<ICustomerListData[]>;
	}

	protected setupFadeoutEffect() {
		const el = this.$tableWrapper.get(0);
		const threshold = 5;
		const offset = el.scrollWidth - el.offsetWidth;

		if (el.scrollLeft > threshold && offset > threshold) {
			this.$tableWrapper.addClass('left-scroll');
		} else {
			this.$tableWrapper.removeClass('left-scroll');
		}

		if ((offset - el.scrollLeft) > threshold && offset > threshold) {
			this.$tableWrapper.addClass('right-scroll');
		} else {
			this.$tableWrapper.removeClass('right-scroll');
		}

	}

	private addEventHandlers() {
		this.$searchButton.on('click', (evt : JQuery.ClickEvent) => this.onSearch(evt));
		this.$openIncidentsCheckbox.on('click', () => this.onFilterChange());
		this.$closeIncidentsCheckbox.on('click', () => this.onFilterChange());
		this.$exportToCSVButton.on('click', () => this.exportToCSV());
		this.$tableWrapper.on('scroll', () => this.setupFadeoutEffect());
	}

	private onFilterChange() : void {
		this.filterData();
		this.lastSearchTerm = '';
		this.search();
		this.updateResultNumber();
		this.update();
	}

	private onSearch(evt : JQuery.ClickEvent) : void {
		evt.preventDefault();
		this.search();
		this.updateResultNumber();
		this.update();
	}

	private sort() {
		const cmp = comparingBy(this.data[0], compare, this.sortPropName);
		this.data.sort(this.sortAsc ? cmp : inverted(cmp));
	}

	private searchHelper(searchTerms : string[]) {
		const includeSearchTerms = searchTerms.filter(searchTerm => !searchTerm.startsWith('-'));
		const excludeSearchTerms = searchTerms.filter(searchTerm => searchTerm.startsWith('-'))
			.map(searchTerm => searchTerm.substring(1));

		for (const item of this.data) {
			if(item.display) {
				item.display = !excludeSearchTerms.some(searchTerm => item.searchValue!.includes(searchTerm)) &&
					includeSearchTerms.every(searchTerm => item.searchValue!.includes(searchTerm));
			}
		}
	}

	private updateResultNumber() {
		this.$resultNumberHolder.text(this.data.filter(item => item.display).length);
	}
}



// WEBPACK FOOTER //
// typescript://../clientlibs/js/lib/pex/Tc.Module.AbstractPexList.ts